/**
 * PaperManagement Component
 * 
 * Admin component for managing paper submissions across conferences.
 * Features:
 * - Conference selection dropdown
 * - Paper listing with status management
 * - Status editing for active conferences only
 * - Excel export functionality with paper details and file links
 * 
 * @component
 */

import React, { useState, useEffect, useRef } from 'react';
import api, { 
    getActiveConferences, 
    getPapersByConference, 
    updatePaperStatus as updatePaperStatusAPI, 
    updatePaper, 
    downloadAllManuscripts,
    deleteAdminPaper 
} from '../../services/api';
import { researchAreas } from '../../constants/types';

// Heavy spreadsheet libraries are lazy-loaded to keep initial bundle lean
let ExcelJSPromise = null; // cache dynamic import promise for ExcelJS (export)
let XLSXPromise = null;    // cache dynamic import promise for SheetJS (bulk upload)
import PaperDetailsModal from './PaperDetailsModal';
import PaperEditModal from './PaperEditModal';
import ManualRegistrationModal from './ManualRegistrationModal';
import ManualAttendeeRegistrationModal from './ManualAttendeeRegistrationModal';

const PaperManagement = () => {
    const [conferences, setConferences] = useState([]);
    const [selectedConference, setSelectedConference] = useState('');
    const [selectedConferenceYear, setSelectedConferenceYear] = useState('');
    const [papers, setPapers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState({});
    const [selectedPaper, setSelectedPaper] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showManualRegistrationModal, setShowManualRegistrationModal] = useState(false);
    const [showManualAttendeeRegistrationModal, setShowManualAttendeeRegistrationModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadResults, setUploadResults] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [exportingExcel, setExportingExcel] = useState(false);
    // State to track pending status changes before saving
    const [pendingStatusChanges, setPendingStatusChanges] = useState({});
    const [savingChanges, setSavingChanges] = useState(false);
    const mountedRef = useRef(true);
    // Store original paper data to track changes against
    const originalPapersRef = useRef([]);

    // Cleanup function to prevent memory leaks
    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Paper status options
    const statusOptions = [
        { value: 'under_review', label: 'Under Review', className: 'bg-status-under-review' },
        { value: 'accepted', label: 'Accepted', className: 'bg-status-accepted' },
        { value: 'rejected', label: 'Rejected', className: 'bg-status-rejected' },
        { value: 'incomplete', label: 'Update-Revise', className: 'bg-status-incomplete' },
        { value: 'withdrawn', label: 'Withdrawn', className: 'bg-status-withdrawn' }
    ];

    const statusOptionsPositionMap = ['under_review', 'accepted', 'rejected', 'incomplete', 'withdrawn'];
    const RESEND_EMAIL_OPTION_VALUE = '__resend_email__';


    // Load conferences on component mount
    useEffect(() => {
        loadConferences();
    }, []);

    // Load papers when conference is selected
    useEffect(() => {
        if (selectedConference) {
            const selectedConf = conferences.find(conf => conf._id === selectedConference);
            setSelectedConferenceYear((new Date(selectedConf.startDate).getFullYear()).toString().substring(2,5));
            loadPapers();
        } else {
            setPapers([]);
        }
    }, [selectedConference]);

    /**
     * Fetch all conferences from the API
     */
    const loadConferences = async () => {
        try {
            const conferenceList = await getActiveConferences();
            
            setConferences(conferenceList);
            
            // Auto-select the most recent conference (by start date)
            if (conferenceList.length > 0) {
                const mostRecentConference = conferenceList
                    .filter(conf => conf.startDate) // Only conferences with start dates
                    .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))[0]; // Sort by start date descending
                
                if (mostRecentConference) {
                    setSelectedConference(mostRecentConference._id);
                    setSelectedConferenceYear((new Date(mostRecentConference.startDate).getFullYear()).toString().substring(2,5));
                }
            }
        } catch (error) {
            console.error('Error loading conferences:', error);
            setError('Failed to load conferences');
        }
    };

    /**
     * Fetch papers for the selected conference
     */
    const loadPapers = async () => {
        if (!selectedConference) return;
        
        setLoading(true);
        setError('');
        // Clear any pending changes when loading new papers
        setPendingStatusChanges({});
        
        try {
            const papers = await getPapersByConference(selectedConference);
            
            setPapers(papers);
            // Store original data for change tracking
            originalPapersRef.current = papers.map(paper => ({ ...paper }));
        } catch (error) {
            console.error('Error loading papers:', error);
            
            setError('Failed to load papers for this conference');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Track status changes locally without saving immediately
     * This prevents automatic emails and allows for manual confirmation
     */
    const updatePaperStatus = (paperId, newStatus) => {
        // Find the original status from the stored original data
        const originalPaper = originalPapersRef.current.find(paper => paper._id === paperId);
        const originalStatus = originalPaper?.status;
        
        // Update local state immediately for UI feedback
        setPapers(prev => prev.map(paper => 
            paper._id === paperId ? { ...paper, status: newStatus } : paper
        ));
        
        // Track the change for later saving
        if (newStatus !== originalStatus) {
            setPendingStatusChanges(prev => ({
                ...prev,
                [paperId]: {
                    originalStatus,
                    newStatus,
                    paperTitle: originalPaper?.manuscriptTitle
                }
            }));
        } else {
            // If status is reverted to original, remove from pending changes
            setPendingStatusChanges(prev => {
                const updated = { ...prev };
                delete updated[paperId];
                return updated;
            });
        }
    };

    const resendStatusEmail = async (paper) => {
        if (pendingStatusChanges[paper._id]) {
            alert('Please save or discard the pending status change before resending the email.');
            return;
        }

        if (!['accepted', 'rejected', 'withdrawn', 'incomplete'].includes(paper.status)) {
            alert('No status email is configured for this status.');
            return;
        }

        const confirmMessage = `Resend the current status email (${paper.status}) for "${paper.manuscriptTitle}"?`;
        if (!window.confirm(confirmMessage)) {
            return;
        }

        setError('');
        try {
            await updatePaperStatusAPI(paper._id, paper.status);
            alert('Status email resent.');
        } catch (error) {
            console.error('Error resending status email:', error);
            setError('Failed to resend status email. Please try again.');
        }
    };

    const handleStatusSelectChange = (paper, value) => {
        if (value === RESEND_EMAIL_OPTION_VALUE) {
            resendStatusEmail(paper);
            return;
        }

        updatePaperStatus(paper._id, value);
    };

    /**
     * Save all pending status changes to the server
     * This will trigger email notifications for status changes
     */
    const savePendingStatusChanges = async () => {
        const pendingChangesCount = Object.keys(pendingStatusChanges).length;
        if (pendingChangesCount === 0) return;

        // Confirm action since this will trigger emails
        const changesList = Object.values(pendingStatusChanges)
            .map(change => `• ${change.paperTitle}: ${change.originalStatus} → ${change.newStatus}`)
            .join('\n');
        
        const confirmMessage = `Are you sure you want to save these ${pendingChangesCount} status change(s)?\n\nThis will send email notifications to authors:\n\n${changesList}`;
        
        if (!window.confirm(confirmMessage)) {
            return;
        }

        setSavingChanges(true);
        setError('');
        
        try {
            const promises = Object.entries(pendingStatusChanges).map(async ([paperId, change]) => {
                try {
                    await updatePaperStatusAPI(paperId, change.newStatus);
                    return { paperId, success: true };
                } catch (error) {
                    console.error(`Failed to update paper ${paperId}:`, error);
                    return { paperId, success: false, error: error.message };
                }
            });

            const results = await Promise.all(promises);
            const failedUpdates = results.filter(result => !result.success);

            if (failedUpdates.length > 0) {
                // Some updates failed - revert those papers and show error
                failedUpdates.forEach(({ paperId }) => {
                    const originalPaper = originalPapersRef.current.find(paper => paper._id === paperId);
                    if (originalPaper) {
                        setPapers(prev => prev.map(paper => 
                            paper._id === paperId ? { ...paper, status: originalPaper.status } : paper
                        ));
                    }
                });

                setError(`Failed to update ${failedUpdates.length} paper(s). Please try again.`);
                
                // Keep only failed changes in pending state
                const remainingChanges = {};
                failedUpdates.forEach(({ paperId }) => {
                    remainingChanges[paperId] = pendingStatusChanges[paperId];
                });
                setPendingStatusChanges(remainingChanges);
            } else {
                // All updates successful - update original data reference
                originalPapersRef.current = originalPapersRef.current.map(originalPaper => {
                    const change = pendingStatusChanges[originalPaper._id];
                    return change ? { ...originalPaper, status: change.newStatus } : originalPaper;
                });
                setPendingStatusChanges({});
                setError('');
            }

        } catch (error) {
            console.error('Error saving status changes:', error);
            setError('Failed to save status changes. Please try again.');
        } finally {
            setSavingChanges(false);
        }
    };

    /**
     * Discard all pending status changes and revert to original statuses
     */
    const discardPendingChanges = () => {
        if (Object.keys(pendingStatusChanges).length === 0) return;

        if (!window.confirm('Are you sure you want to discard all pending changes?')) {
            return;
        }

        // Revert all papers to their original status using the stored original data
        setPapers(prev => prev.map(paper => {
            const originalPaper = originalPapersRef.current.find(orig => orig._id === paper._id);
            return originalPaper ? { ...paper, status: originalPaper.status } : paper;
        }));

        // Clear pending changes
        setPendingStatusChanges({});
    };

    // Helper function to find category for a given topic
    const findCategoryForTopic = (topic) => {
        if (!topic) return "";
        for (const [category, topics] of Object.entries(researchAreas)) {
          if (topics.includes(topic)) {
            return category;
          }
        }
        return 'N/A';
    };

        const mergeResearchAreaLabel = (topic) => {
                if (!topic) return 'N/A';

                const category = findCategoryForTopic(topic);
                const categoryPrefix = category && category !== 'N/A'
                        ? category.split(/\s+/)[0]
                        : '';

                return [categoryPrefix, topic].filter(Boolean).join(' ').trim() || 'N/A';
        };

        const getParticipantResearchAreas = (registration) => {
                const primaryAreas = registration?.userId?.areasOfExpertise?.primary || [];
                const secondaryAreas = registration?.userId?.areasOfExpertise?.secondary || [];
                return [...primaryAreas, ...secondaryAreas].filter(Boolean).slice(0, 3);
        };

    // Build a registration lookup keyed by paper/email for reuse across exports
    const normalizeLookupValue = (value) => String(value || '').trim().toLowerCase();

    const buildAuthorRegistrationLookupKeys = (author) => {
        const emailKeys = [author?.email, author?.secondaryEmail]
            .map(normalizeLookupValue)
            .filter(Boolean);

        const nameKey = [author?.firstName, author?.lastName]
            .map(normalizeLookupValue)
            .filter(Boolean)
            .join(' ');

        return { emailKeys, nameKey };
    };

    const buildRegistrationEmailKeys = (registration) => {
        return [
            registration?.conferenceParticipant?.email,
            registration?.conferenceParticipant?.secondaryEmail,
            registration?.userId?.email,
            registration?.userId?.secondaryEmail
        ]
            .map(normalizeLookupValue)
            .filter(Boolean)
            .filter((emailKey, index, emailKeys) => emailKeys.indexOf(emailKey) === index);
    };

    const findRegistrationMatchForAuthor = (registrationsByPaperAndEmail, registrationsByPaperAndName, paperId, author) => {
        if (!paperId || !author) {
            return null;
        }

        const { emailKeys, nameKey } = buildAuthorRegistrationLookupKeys(author);
        
        for (const emailKey of emailKeys) {
            const emailMatch = registrationsByPaperAndEmail[`${paperId}__${emailKey}`] || null;
            if (emailMatch) {
                return emailMatch;
            }
        }

        if (nameKey) {
            return registrationsByPaperAndName[`${paperId}__${nameKey}`] || null;
        }

        return null;
    };

    const buildRegistrationIndex = async () => {
        const registrationsByPaperAndEmail = {};
        const registrationsByPaperAndName = {};
        const standaloneRegistrations = [];
        try {
            const regRes = await api.get(`/registration/conference/${selectedConference}`);
            const regs = Array.isArray(regRes.data) ? regRes.data : [];
            
            regs.forEach(r => {
                if (r.paymentStatus === 'completed' || r.registrationStatus === 'confirmed') {
                    const paperId = r.paperId?._id;
                    const participantEmailKeys = buildRegistrationEmailKeys(r);
                    const participantNameKey = [r.userId?.firstName || r.conferenceParticipant?.firstName, r.userId?.lastName || r.conferenceParticipant?.lastName]
                        .map(normalizeLookupValue)
                        .filter(Boolean)
                        .join(' ');

                        if (r.paperId) {
                        participantEmailKeys.forEach(participantEmailKey => {
                            const compositeKey = `${paperId}__${participantEmailKey}`;
                            if (!registrationsByPaperAndEmail[compositeKey]) {
                                registrationsByPaperAndEmail[compositeKey] = {
                                    amount: r.paymentAmount,
                                    currency: r.paymentCurrency || r.paymentAmountCurrency || 'EUR',
                                    accompaniedPersonName: r.accompaniedPersonName || ''
                                };
                            }
                        });

                        if (participantNameKey) {
                            const compositeNameKey = `${paperId}__${participantNameKey}`;
                            if (!registrationsByPaperAndName[compositeNameKey]) {
                                registrationsByPaperAndName[compositeNameKey] = {
                                    amount: r.paymentAmount,
                                    currency: r.paymentCurrency || r.paymentAmountCurrency || 'EUR',
                                    accompaniedPersonName: r.accompaniedPersonName || ''
                                };
                            }
                        }
                    } else {
                        standaloneRegistrations.push(r);
                    }
                }
            });
        } catch(regErr) {
            console.warn('Could not load registrations for export (continuing with N/A fees):', regErr.message);
        }
        return { registrationsByPaperAndEmail, registrationsByPaperAndName, standaloneRegistrations };
    };
    
    /**
     * Generate and download Excel file with paper data using ExcelJS
     */
    const exportToExcel = async () => {
        if (papers.length === 0) {
            alert('No papers to export');
            return;
        }

        setExportingExcel(true);
        setError('');

        try {
            const { registrationsByPaperAndEmail, registrationsByPaperAndName, standaloneRegistrations } = await buildRegistrationIndex();
            
            const selectedConf = conferences.find(conf => conf._id === selectedConference);        
            const fileName = `${selectedConf?.title || 'Conference'}_Papers_${new Date().toISOString().split('T')[0]}.xlsx`;

            // Dynamically import ExcelJS only when needed
            if (!ExcelJSPromise) {
                ExcelJSPromise = import('exceljs');
            }
            const ExcelJSImport = await ExcelJSPromise;
            const ExcelJS = ExcelJSImport.default || ExcelJSImport; // compatibility
            // Create workbook
            const workbook = new ExcelJS.Workbook();
            
            // Set workbook properties
            workbook.creator = 'MFS Conference System';
            workbook.lastModifiedBy = 'Admin';
            workbook.created = new Date();
            workbook.modified = new Date();

            // Create main worksheet
            const worksheet = workbook.addWorksheet('Papers', {
                properties: { tabColor: { argb: 'FF0000FF' } }
            });

        // Define columns including first 6 authors
        const columns = [
            { header: 'MS#', key: 'msNumber', width: 25 },
            { header: 'MSTitle', key: 'msTitle', width: 40 },
            { header: 'Abstract', key: 'abstract', width: 50 },
            { header: 'Decision', key: 'status', width: 10 },
            { header: 'CAuthor', key: 'cAuthor', width: 10 },
            { header: 'MSType', key: 'msType', width: 10 },
            { header: 'SDate', key: 'submissionDate', width: 15 },
            { header: 'REGxyz', key: 'regxyz', width: 10 },
            { header: 'Session Code', key: 'sessionCode', width: 10 },
            { header: 'MRS', key: 'mrs', width: 10 },
            { header: 'Session Title', key: 'sessionTitle', width: 10 },
            { header: 'Place', key: 'place', width: 10 },
            { header: 'Notes', key: 'notes', width: 40 },
            { header: 'SRA1', key: 'sra1', width: 20 },
            { header: 'SRA2', key: 'sra2', width: 20 },
            { header: 'SRA3', key: 'sra3', width: 20 },
            { header: 'Paper Awards', key: 'paperAwards', width: 50 },
            // { header: 'Full Manuscript', key: 'fullManuscript', width: 25 },
            // { header: 'Blind Copy', key: 'blindCopy', width: 25 }
        ];

        // Add columns for first 6 authors
        for (let i = 1; i <= 6; i++) {
            columns.push(
                { header: `FName${i}`, key: `fname${i}`, width: 15 },
                { header: `LName${i}`, key: `lname${i}`, width: 15 },
                { header: `Request${i}`, key: `request${i}`, width: 12 },
                { header: `RegFee${i}`, key: `regfee${i}`, width: 12 },
                { header: `Currency${i}`, key: `currency${i}`, width: 12 },
                { header: `Rank${i}`, key: `rank${i}`, width: 20 },
                { header: `Affiliation${i}`, key: `affiliation${i}`, width: 25 },
                { header: `Country${i}`, key: `country${i}`, width: 15 },
                { header: `Email${i}`, key: `email${i}`, width: 25 }
            );
        }

        columns.push(
            { header: `FNameC`, key: `fnameC`, width: 15 },
            { header: `LNameC`, key: `lnameC`, width: 15 },
            { header: `RequestC`, key: `requestC`, width: 12 },
            { header: `RegFeeC`, key: `regfeeC`, width: 12 },
            { header: `CurrencyC`, key: `currencyC`, width: 12 },
            { header: `RankC`, key: `rankC`, width: 20 },
            { header: `AffiliationC`, key: `affiliationC`, width: 25 },
            { header: `CountryC`, key: `countryC`, width: 15 },
            { header: `EmailC`, key: `emailC`, width: 25 }
        );

        columns.push(
            { header: `FNameD`, key: `fnameD`, width: 15 },
            { header: `LNameD`, key: `lnameD`, width: 15 },
            { header: `RequestD`, key: `requestD`, width: 12 },
            { header: `RegFeeD`, key: `regfeeD`, width: 12 },
            { header: `CurrencyD`, key: `currencyD`, width: 12 },
            { header: `RankD`, key: `rankD`, width: 20 },
            { header: `AffiliationD`, key: `affiliationD`, width: 25 },
            { header: `CountryD`, key: `countryD`, width: 15 },
            { header: `EmailD`, key: `emailD`, width: 25 }
        );

        columns.push(
            { header: 'Paper ID', key: 'paperID', width: 25 },
            { header: 'URL', key: 'url', width: 25 },
            { header: '#END#', key: 'end', width: 5 }
        );

        worksheet.columns = columns;

        // Style the header row
        const headerRow = worksheet.getRow(1);
        headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF366092' }
        };
        headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
        headerRow.height = 25;

        // Highlight Status column header (Column D now)
        // const statusHeaderCell = worksheet.getCell('D1');
        // statusHeaderCell.fill = {
        //     type: 'pattern',
        //     pattern: 'solid',
        //     fgColor: { argb: 'FFFFFF00' } // Yellow
        // };
        // statusHeaderCell.font = { bold: true, color: { argb: 'FF000000' } };
       
        // Add data rows with pre-signed download URLs
        for (let paperIndex = 0; paperIndex < papers.length; paperIndex++) {
            const paper = papers[paperIndex];
            const authors = paper.authors || [];

            // Generate download token for this paper if it has a fullManuscript
            let downloadUrl = 'N/A';
            if (paper.files?.fullManuscript?.filename && paper._id) {
                try {
                    const tokenResponse = await api.get(`/papers/${paper._id}/generate-download-token/fullManuscript`);
                    downloadUrl = tokenResponse.data.downloadUrl;
                } catch (error) {
                    console.error(`Failed to generate download token for paper ${paper._id}:`, error);
                    downloadUrl = 'Error generating download link';
                }
            }

            // Find the corresponding author index (1-based)                
            const correspondingAuthorIndex = authors.findIndex(author => author.isCorresponding || author.isCorrespondingAuthor);
            const cAuthorValue = correspondingAuthorIndex !== -1 ? correspondingAuthorIndex + 1 : 'N/A';
            

            let REGxyz=''
            const accompaniedNames = new Set();
            // Add first 6 authors data
            for (let i = 1; i <= 6; i++) {
                const author = authors[i - 1]; // Array is 0-indexed
                if (author) {
                    const regMatch = findRegistrationMatchForAuthor(
                        registrationsByPaperAndEmail,
                        registrationsByPaperAndName,
                        paper._id,
                        author
                    );

                    if (regMatch) {
                        REGxyz+=`P${i}`;
                        if (regMatch.accompaniedPersonName) {
                            accompaniedNames.add(regMatch.accompaniedPersonName);
                        }
                    }
                }
            }

            const accompaniedNote = accompaniedNames.size > 0
                ? `Accompanied: ${Array.from(accompaniedNames).join('; ')}`
                : '';
            const combinedNotes = [paper.adminNotes || '', accompaniedNote].filter(Boolean).join(' | ');

            const rowData = {
                msNumber: `MC${selectedConferenceYear}~${paper.paperNumber + 101}`, // Use stored paperNumber + 101 for display
                msTitle: paper.manuscriptTitle || 'N/A',
                abstract: paper.manuscriptAbstract || 'N/A',
                status: statusOptionsPositionMap.indexOf(paper.status),
                cAuthor: cAuthorValue,
                msType: paper.manuscriptType?.substring(0,1) || 'N/A',
                submissionDate: paper.submissionDate ? new Date(paper.submissionDate).toISOString().split('T')[0] : 'N/A',
                regxyz: REGxyz || 'N/A',
                sessionCode: 'N/A',
                mrs: 'N/A',
                sessionTitle: 'N/A',
                place: 'N/A',
                notes: combinedNotes,
                sra1: mergeResearchAreaLabel(paper.researchAreas?.[0]),
                sra2: mergeResearchAreaLabel(paper.researchAreas?.[1]),
                sra3: mergeResearchAreaLabel(paper.researchAreas?.[2]),
                paperAwards: 'N/A',
            };

            // Add first 6 authors data
            for (let i = 1; i <= 6; i++) {
                const author = authors[i - 1]; // Array is 0-indexed
                if (author) {
                    rowData[`fname${i}`] = author.firstName || 'N/A';
                    rowData[`lname${i}`] = author.lastName || 'N/A';
                    rowData[`request${i}`] = 'N/A';
                    const regMatch = findRegistrationMatchForAuthor(
                        registrationsByPaperAndEmail,
                        registrationsByPaperAndName,
                        paper._id,
                        author
                    );
                    rowData[`regfee${i}`] = regMatch ? regMatch.amount : 'N/A';
                    rowData[`currency${i}`] = regMatch ? regMatch.currency : 'N/A';
                    
                    // Enhanced field access for logged-in user vs co-authors
                    // Try multiple possible field names for rank
                    rowData[`rank${i}`] = author.primaryRank || 
                                         author.rank ||
                                         author.rankTitle || 
                                         author.academicRank || 
                                         author.position ||
                                         author.primaryAffiliation?.rank ||
                                         'N/A';
                    
                    // Try multiple possible field names for affiliation/institution
                    rowData[`affiliation${i}`] = author.primaryInstitution || 
                                                author.institution || 
                                                author.affiliation || 
                                                author.primaryAffiliation?.institution ||
                                                author.university || 
                                                author.organization ||
                                                author.company ||
                                                'N/A';
                    
                    // Try multiple possible field names for country
                    rowData[`country${i}`] = author.primaryCountry || 
                                            author.country || 
                                            author.nationality ||
                                            author.countryCode ||
                                            author.primaryAffiliation?.address?.country ||
                                            'N/A';
                    
                    rowData[`email${i}`] = author.email || 'N/A';
                } else {
                    // Fill with N/A if author doesn't exist
                    rowData[`fname${i}`] = 'N/A';
                    rowData[`lname${i}`] = 'N/A';
                    rowData[`request${i}`] = 'N/A';
                    rowData[`regfee${i}`] = 'N/A';
                    rowData[`currency${i}`] = 'N/A';
                    rowData[`rank${i}`] = 'N/A';
                    rowData[`affiliation${i}`] = 'N/A';
                    rowData[`country${i}`] = 'N/A';
                    rowData[`email${i}`] = 'N/A';
                }
            }
           
            rowData[`fnameC`] = 'N/A';
            rowData[`lnameC`] = 'N/A';
            rowData[`requestC`] = 'N/A';
            rowData[`regfeeC`] = 'N/A';
            rowData[`currencyC`] = 'N/A';
            rowData[`rankC`] = 'N/A';
            rowData[`affiliationC`] = 'N/A';
            rowData[`countryC`] = 'N/A';
            rowData[`emailC`] = 'N/A';

            rowData[`fnameD`] = 'N/A';
            rowData[`lnameD`] = 'N/A';
            rowData[`requestD`] = 'N/A';
            rowData[`regfeeD`] = 'N/A';
            rowData[`currencyD`] = 'N/A';
            rowData[`rankD`] = 'N/A';
            rowData[`affiliationD`] = 'N/A';
            rowData[`countryD`] = 'N/A';
            rowData[`emailD`] = 'N/A';

            rowData[`paperID`] = paper._id || 'N/A';
            rowData[`end`] = '#END#';

            const addedRow = worksheet.addRow(rowData);
            
            // Set the URL cell with the pre-signed download URL
            addedRow.getCell(`url`).value = {
                text: downloadUrl === 'N/A' ? 'No file' : 'Download File',
                hyperlink: downloadUrl
            };

            // Style the hyperlink if it's a valid URL
            if (downloadUrl !== 'N/A' && !downloadUrl.includes('Error')) {
                addedRow.getCell(`url`).font = {
                    color: { argb: 'FF0066CC' },
                    underline: true
                };
            }
        }

        // Add standalone registrations (attendees without papers) with MZ prefix
        (standaloneRegistrations || []).forEach((registration, idx) => {
            const participant = registration.conferenceParticipant || {};
            const participantName = [participant.firstName, participant.middleName, participant.lastName]
                .filter(Boolean)
                .join(' ')
                .trim() ||
                [registration.userId?.firstName, registration.userId?.lastName].filter(Boolean).join(' ') ||
                'N/A';

            const researchAreas = getParticipantResearchAreas(registration);

            const rowData = {
                msNumber: `MZ${selectedConferenceYear}~${idx + 101}`,
                msTitle: 'Standalone Registration',
                abstract: 'N/A',
                status: 'N/A',
                cAuthor: 'N/A',
                msType: 'N/A',
                submissionDate: registration.registrationDate ? new Date(registration.registrationDate).toISOString().split('T')[0] : 'N/A',
                regxyz: 'P1',
                sessionCode: 'N/A',
                mrs: 'N/A',
                sessionTitle: 'N/A',
                place: 'N/A',
                notes: [registration.notes || '', registration.accompaniedPersonName ? `Accompanied: ${registration.accompaniedPersonName}` : ''].filter(Boolean).join(' | ') || 'N/A',
                sra1: mergeResearchAreaLabel(researchAreas[0]),
                sra2: mergeResearchAreaLabel(researchAreas[1]),
                sra3: mergeResearchAreaLabel(researchAreas[2]),
                paperAwards: 'N/A'
            };

            // Fill participant info in first author position
            rowData[`fname1`] = participant.firstName || registration.userId?.firstName || 'N/A';
            rowData[`lname1`] = participant.lastName || registration.userId?.lastName || 'N/A';
            rowData[`request1`] = 'N/A';
            rowData[`regfee1`] = typeof registration.paymentAmount === 'number' ? registration.paymentAmount : 'N/A';
            rowData[`currency1`] = registration.paymentCurrency || 'EUR';
            rowData[`rank1`] = 'N/A';
            rowData[`affiliation1`] = 'N/A';
            rowData[`country1`] = 'N/A';
            rowData[`email1`] = participant.email || registration.userId?.email || 'N/A';

            // Fill remaining author positions with N/A
            for (let i = 2; i <= 6; i++) {
                rowData[`fname${i}`] = 'N/A';
                rowData[`lname${i}`] = 'N/A';
                rowData[`request${i}`] = 'N/A';
                rowData[`regfee${i}`] = 'N/A';
                rowData[`currency${i}`] = 'N/A';
                rowData[`rank${i}`] = 'N/A';
                rowData[`affiliation${i}`] = 'N/A';
                rowData[`country${i}`] = 'N/A';
                rowData[`email${i}`] = 'N/A';
            }

            rowData[`fnameC`] = 'N/A';
            rowData[`lnameC`] = 'N/A';
            rowData[`requestC`] = 'N/A';
            rowData[`regfeeC`] = 'N/A';
            rowData[`currencyC`] = 'N/A';
            rowData[`rankC`] = 'N/A';
            rowData[`affiliationC`] = 'N/A';
            rowData[`countryC`] = 'N/A';
            rowData[`emailC`] = 'N/A';

            rowData[`fnameD`] = 'N/A';
            rowData[`lnameD`] = 'N/A';
            rowData[`requestD`] = 'N/A';
            rowData[`regfeeD`] = 'N/A';
            rowData[`currencyD`] = 'N/A';
            rowData[`rankD`] = 'N/A';
            rowData[`affiliationD`] = 'N/A';
            rowData[`countryD`] = 'N/A';
            rowData[`emailD`] = 'N/A';

            rowData[`paperID`] = registration._id || 'N/A';
            rowData[`end`] = '#END#';

            const addedRow = worksheet.addRow(rowData);
            addedRow.getCell(`url`).value = 'N/A';
        });

        // Generate buffer and download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);        } catch (error) {
            console.error('Error generating Excel file:', error);
            setError('Failed to generate Excel file. Please try again.');
        } finally {
            setExportingExcel(false);
        }
    };

    /**
     * Generate and download Excel file with one row per author (paper info repeated)
     */
    const exportAuthorsToExcel = async () => {
        if (papers.length === 0) {
            alert('No papers to export');
            return;
        }

        setExportingExcel(true);
        setError('');

        try {
            const { registrationsByPaperAndEmail, registrationsByPaperAndName, standaloneRegistrations } = await buildRegistrationIndex();
            const selectedConf = conferences.find(conf => conf._id === selectedConference);
            const fileName = `${selectedConf?.title || 'Conference'}_Authors_${new Date().toISOString().split('T')[0]}.xlsx`;

            if (!ExcelJSPromise) {
                ExcelJSPromise = import('exceljs');
            }
            const ExcelJSImport = await ExcelJSPromise;
            const ExcelJS = ExcelJSImport.default || ExcelJSImport;

            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'MFS Conference System';
            workbook.lastModifiedBy = 'Admin';
            workbook.created = new Date();
            workbook.modified = new Date();

            const worksheet = workbook.addWorksheet('Authors', {
                properties: { tabColor: { argb: 'FF228B22' } }
            });

            const columns = [
                { header: 'MS#', key: 'msNumber', width: 25 },
                { header: 'MSTitle', key: 'msTitle', width: 40 },
                { header: 'Abstract', key: 'abstract', width: 50 },
                { header: 'Decision', key: 'status', width: 10 },
                { header: 'CAuthor', key: 'cAuthor', width: 10 },
                { header: 'MSType', key: 'msType', width: 10 },
                { header: 'SDate', key: 'submissionDate', width: 15 },
                { header: 'AuthorOrder', key: 'authorOrder', width: 12 },
                { header: 'Corresponding', key: 'isCorresponding', width: 15 },
                { header: 'FName', key: 'fname', width: 15 },
                { header: 'LName', key: 'lname', width: 15 },
                { header: 'Request', key: 'request', width: 12 },
                { header: 'RegFee', key: 'regfee', width: 12 },
                { header: 'Currency', key: 'currency', width: 12 },
                { header: 'Rank', key: 'rank', width: 20 },
                { header: 'Affiliation', key: 'affiliation', width: 25 },
                { header: 'Country', key: 'country', width: 15 },
                { header: 'Email', key: 'email', width: 25 },
                { header: 'REGxyz', key: 'regxyz', width: 10 },
                { header: 'Session Code', key: 'sessionCode', width: 10 },
                { header: 'MRS', key: 'mrs', width: 10 },
                { header: 'Session Title', key: 'sessionTitle', width: 10 },
                { header: 'Place', key: 'place', width: 10 },
                { header: 'Notes', key: 'notes', width: 40 },
                { header: 'SRA1', key: 'sra1', width: 20 },
                { header: 'SRA2', key: 'sra2', width: 20 },
                { header: 'SRA3', key: 'sra3', width: 20 },
                { header: 'Paper Awards', key: 'paperAwards', width: 50 },
                { header: 'Paper ID', key: 'paperID', width: 25 },
                { header: 'URL', key: 'url', width: 25 },
                { header: '#END#', key: 'end', width: 5 }
            ];

            worksheet.columns = columns;

            const headerRow = worksheet.getRow(1);
            headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF366092' }
            };
            headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
            headerRow.height = 25;

            for (let paperIndex = 0; paperIndex < papers.length; paperIndex++) {
                const paper = papers[paperIndex];
                const authors = paper.authors || [];

                let downloadUrl = 'N/A';
                if (paper.files?.fullManuscript?.filename && paper._id) {
                    try {
                        const tokenResponse = await api.get(`/papers/${paper._id}/generate-download-token/fullManuscript`);
                        downloadUrl = tokenResponse.data.downloadUrl;
                    } catch (error) {
                        console.error(`Failed to generate download token for paper ${paper._id}:`, error);
                        downloadUrl = 'Error generating download link';
                    }
                }

                const correspondingAuthorIndex = authors.findIndex(author => author.isCorresponding || author.isCorrespondingAuthor);
                const cAuthorValue = correspondingAuthorIndex !== -1 ? correspondingAuthorIndex + 1 : 'N/A';

                const baseRowData = {
                    msNumber: `MC${selectedConferenceYear}~${paper.paperNumber + 101}`,
                    msTitle: paper.manuscriptTitle || 'N/A',
                    abstract: paper.manuscriptAbstract || 'N/A',
                    status: statusOptionsPositionMap.indexOf(paper.status),
                    cAuthor: cAuthorValue,
                    msType: paper.manuscriptType?.substring(0,1) || 'N/A',
                    submissionDate: paper.submissionDate ? new Date(paper.submissionDate).toISOString().split('T')[0] : 'N/A',
                    sessionCode: 'N/A',
                    mrs: 'N/A',
                    sessionTitle: 'N/A',
                    place: 'N/A',
                    notes: paper.adminNotes || '',
                    sra1: mergeResearchAreaLabel(paper.researchAreas?.[0]),
                    sra2: mergeResearchAreaLabel(paper.researchAreas?.[1]),
                    sra3: mergeResearchAreaLabel(paper.researchAreas?.[2]),
                    paperAwards: 'N/A',
                    paperID: paper._id || 'N/A',
                    end: '#END#'
                };

                const addAuthorRow = (author, authorIndex) => {
                    const regMatch = findRegistrationMatchForAuthor(
                        registrationsByPaperAndEmail,
                        registrationsByPaperAndName,
                        paper._id,
                        author
                    );

                    const accompaniedNote = regMatch?.accompaniedPersonName
                        ? `Accompanied: ${regMatch.accompaniedPersonName}`
                        : '';
                    const combinedNotes = [baseRowData.notes || '', accompaniedNote].filter(Boolean).join(' | ');

                    const rowData = {
                        ...baseRowData,
                        authorOrder: author ? authorIndex + 1 : 'N/A',
                        isCorresponding: author ? (authorIndex === correspondingAuthorIndex ? 'Yes' : 'No') : 'N/A',
                        fname: author?.firstName || 'N/A',
                        lname: author?.lastName || 'N/A',
                        request: 'N/A',
                        regfee: regMatch ? regMatch.amount : 'N/A',
                        currency: regMatch ? regMatch.currency : 'N/A',
                        rank: author?.primaryRank || author?.rank || author?.rankTitle || author?.academicRank || author?.position || author?.primaryAffiliation?.rank || 'N/A',
                        affiliation: author?.primaryInstitution || author?.institution || author?.affiliation || author?.primaryAffiliation?.institution || author?.university || author?.organization || author?.company || 'N/A',
                        country: author?.primaryCountry || author?.country || author?.nationality || author?.countryCode || author?.primaryAffiliation?.address?.country || 'N/A',
                        email: author?.email || 'N/A',
                        regxyz: regMatch ? `P${authorIndex + 1}` : 'N/A',
                        notes: combinedNotes
                    };

                    const addedRow = worksheet.addRow(rowData);
                    addedRow.getCell('url').value = {
                        text: downloadUrl === 'N/A' ? 'No file' : 'Download File',
                        hyperlink: downloadUrl
                    };

                    if (downloadUrl !== 'N/A' && !downloadUrl.includes('Error')) {
                        addedRow.getCell('url').font = {
                            color: { argb: 'FF0066CC' },
                            underline: true
                        };
                    }
                };

                if (authors.length === 0) {
                    addAuthorRow(null, 0);
                } else {
                    for (let i = 0; i < authors.length; i++) {
                        addAuthorRow(authors[i], i);
                    }
                }
            }

            // Add standalone registrations (attendees without papers) with MZ prefix
            (standaloneRegistrations || []).forEach((registration, idx) => {
                const participant = registration.conferenceParticipant || {};
                const researchAreas = getParticipantResearchAreas(registration);

                const rowData = {
                    msNumber: `MZ${selectedConferenceYear}~${idx + 101}`,
                    msTitle: 'Standalone Registration',
                    abstract: 'N/A',
                    status: 'N/A',
                    cAuthor: 'N/A',
                    msType: 'N/A',
                    submissionDate: registration.registrationDate ? new Date(registration.registrationDate).toISOString().split('T')[0] : 'N/A',
                    sessionCode: 'N/A',
                    mrs: 'N/A',
                    sessionTitle: 'N/A',
                    place: 'N/A',
                    notes: [registration.notes || '', registration.accompaniedPersonName ? `Accompanied: ${registration.accompaniedPersonName}` : ''].filter(Boolean).join(' | ') || 'N/A',
                    sra1: mergeResearchAreaLabel(researchAreas[0]),
                    sra2: mergeResearchAreaLabel(researchAreas[1]),
                    sra3: mergeResearchAreaLabel(researchAreas[2]),
                    paperAwards: 'N/A',
                    paperID: registration._id || 'N/A',
                    end: '#END#',
                    authorOrder: 1,
                    isCorresponding: 'N/A',
                    fname: participant.firstName || registration.userId?.firstName || 'N/A',
                    lname: participant.lastName || registration.userId?.lastName || 'N/A',
                    request: 'N/A',
                    regfee: typeof registration.paymentAmount === 'number' ? registration.paymentAmount : 'N/A',
                    currency: registration.paymentCurrency || 'EUR',
                    rank: 'N/A',
                    affiliation: 'N/A',
                    country: 'N/A',
                    email: participant.email || registration.userId?.email || 'N/A',
                    regxyz: 'P1'
                };

                const addedRow = worksheet.addRow(rowData);
                addedRow.getCell('url').value = 'N/A';
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating Authors Excel file:', error);
            setError('Failed to generate Authors Excel file. Please try again.');
        } finally {
            setExportingExcel(false);
        }
    };

    /**
     * Handle downloading all manuscripts for the selected conference
     */
    const handleDownloadAll = async () => {
        if (!selectedConference) {
            alert('Please select a conference first');
            return;
        }

        if (papers.length === 0) {
            alert('No papers available for download');
            return;
        }

        try {
            setLoading(true);
            const blob = await downloadAllManuscripts(selectedConference);

            // Create blob URL and trigger download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Set filename with current date
            const filename = `Conference_Papers_${new Date().toISOString().split('T')[0]}.zip`;
                
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error('Error downloading all manuscripts:', error);
            setError('Failed to download all manuscripts. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle Excel file upload for bulk status updates
     */
    const handleBulkUpload = async () => {
        if (!uploadFile) {
            alert('Please select an Excel file first');
            return;
        }

        setUploading(true);
        setUploadResults(null);

        try {
            // Read Excel file on client side
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    // Dynamically import XLSX only when needed
                    if (!XLSXPromise) {
                        XLSXPromise = import('xlsx');
                    }
                    const XLSXImport = await XLSXPromise;
                    const XLSX = XLSXImport.default || XLSXImport;
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const excelData = XLSX.utils.sheet_to_json(worksheet);

                    const validStatuses = ['under_review', 'accepted', 'rejected', 'incomplete', 'withdrawn'];
                    const updateResults = {
                        successful: [],
                        failed: [],
                        skipped: []
                    };

                    let processedCount = 0;

                    // Process each row in the Excel file
                    for (const row of excelData) {
                        const paperId = row.id || row.ID || row.Id;
                        const newStatus = (row.Status || row.status)?.toString().toLowerCase().trim();

                        // Skip rows without required data
                        if (!paperId || !newStatus) {
                            updateResults.skipped.push({
                                row: row,
                                reason: 'Missing paper ID or status'
                            });
                            continue;
                        }

                        // Validate status
                        if (!validStatuses.includes(newStatus)) {
                            updateResults.failed.push({
                                paperId,
                                reason: `Invalid status: ${newStatus}. Valid options: ${validStatuses.join(', ')}`
                            });
                            continue;
                        }

                        try {
                            // Send individual update request using existing API
                            const response = await updatePaperStatusAPI(paperId, newStatus);

                            updateResults.successful.push({
                                paperId,
                                title: response.paper?.manuscriptTitle || 'Unknown',
                                newStatus: newStatus,
                                oldStatus: 'Updated'
                            });

                        } catch (error) {
                            console.error(`Error updating paper ${paperId}:`, error);
                            updateResults.failed.push({
                                paperId,
                                reason: error.response?.data?.message || 'Network error'
                            });
                        }

                        processedCount++;
                    }

                    // Set results and close modal
                    setUploadResults({
                        message: 'Bulk status update completed',
                        summary: {
                            totalRows: excelData.length,
                            successful: updateResults.successful.length,
                            failed: updateResults.failed.length,
                            skipped: updateResults.skipped.length
                        },
                        results: updateResults
                    });

                    setShowUploadModal(false);
                    
                    // Refresh papers list if any updates were successful
                    if (updateResults.successful.length > 0) {
                        await loadPapers();
                    }

                } catch (parseError) {
                    console.error('Error parsing Excel file:', parseError);
                    setError('Failed to parse Excel file. Please check the format and try again.');
                } finally {
                    setUploading(false);
                }
            };

            reader.readAsArrayBuffer(uploadFile);

        } catch (error) {
            console.error('Error processing Excel file:', error);
            setError('Failed to process Excel file. Please try again.');
            setUploading(false);
        }
    };

    /**
     * Check if conference allows status editing
     */
    const isConferenceActive = () => {
        const conference = conferences.find(conf => conf._id === selectedConference);
        if (!conference) return false;
        
        // Define what statuses allow editing
        const editableStatuses = ['Open', 'Active', 'open', 'active'];
        return editableStatuses.some(status => 
            conference.status?.toLowerCase().includes(status.toLowerCase())
        );
    };

    /**
     * Get status badge component
     */
    const getStatusBadge = (status) => {
        const statusConfig = statusOptions.find(option => option.value === status) || statusOptions[0];
        return (
            <span className={`badge ${statusConfig.className} text-white`}>
                {statusConfig.label}
            </span>
        );
    };

    // Get background/text classes for the select based on the status definition
    const getStatusSelectClasses = (status) => {
        const statusConfig = statusOptions.find(option => option.value === status) || statusOptions[0];
        const textClass = statusConfig.className === 'bg-warning' ? 'text-dark' : 'text-white';
        return `${statusConfig.className} ${textClass}`;
    };

    /**
     * Handle file download with authentication
     */
    const handleFileDownload = async (paper, fileType) => {
        try {
            const response = await api.get(`/papers/${paper._id}/download/${fileType}`, {
                responseType: 'blob',
            });

            // Create blob URL and trigger download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Set filename from response headers or use a default
            const filename = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') 
                || paper.files[fileType]?.filename 
                || `${fileType}_${paper._id}.pdf`;
                
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error('Error downloading file:', error);
            setError('Failed to download file. Please try again.');
        }
    };

    /**
     * Handle paper update callback
     */
    const handlePaperUpdated = () => {
        // Reload papers to show updated data
        loadPapers();
    };

    /**
     * Handle paper deletion with confirmation
     */
    const handleDeletePaper = async (paper) => {
        const firstAuthor = paper.authors?.[0] || {};
        const confirmation = window.confirm(
            `Are you sure you want to delete this paper?\n\n` +
            `Title: ${paper.manuscriptTitle}\n` +
            `Author: ${firstAuthor.firstName} ${firstAuthor.lastName}\n` +
            `Paper Number: MS${selectedConferenceYear}~${paper.paperNumber + 101}\n\n` +
            `This action cannot be undone and will permanently delete the paper and all associated files.`
        );

        if (!confirmation) return;

        try {
            await deleteAdminPaper(paper._id);
            
            // Remove paper from local state
            setPapers(prevPapers => prevPapers.filter(p => p._id !== paper._id));
            
            alert('Paper deleted successfully!');
        } catch (error) {
            console.error('Error deleting paper:', error);
            alert('Failed to delete paper. Please try again.');
        }
    };

    /**
     * Filter papers based on search term
     */
    const filteredPapers = papers.filter(paper => {
        if (!searchTerm) return true;
        
        const searchLower = searchTerm.toLowerCase();
        const firstAuthor = paper.authors?.[0] || {};
        
        return (
            paper.manuscriptTitle?.toLowerCase().includes(searchLower) ||
            paper.manuscriptAbstract?.toLowerCase().includes(searchLower) ||
            `${firstAuthor.firstName} ${firstAuthor.lastName}`.toLowerCase().includes(searchLower) ||
            firstAuthor.email?.toLowerCase().includes(searchLower) ||
            paper.status?.toLowerCase().includes(searchLower) ||
            paper.manuscriptType?.toLowerCase().includes(searchLower) ||
            paper.researchAreas?.some(area => area?.toLowerCase().includes(searchLower))
        );
    });

    const LOCALE = 'en-GB';
    const DATE_FORMAT_OPTIONS = { timeZone: 'Etc/GMT' };
    const titleColumnStyle = { minWidth: '320px' };
    const statusColumnStyle = { minWidth: '155px' };
    const authorColumnStyle = { minWidth: '140px' };
    const actionButtonStyle = { minWidth: '40px', flex: '1 1 48%' };
    const headerStickyStyle = {
        position: 'sticky',
        top: 0,
        zIndex: 1030,
        backgroundColor: '#ffffff'
    };

    const formatDate = (dateString, includeYear) => {
        const options = includeYear 
        ? { year: 'numeric', month: 'long', day: 'numeric' }
        : { month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(LOCALE, { ...options, ...DATE_FORMAT_OPTIONS });
    };

    return (
        <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center" style={headerStickyStyle}>
                <h3 className="mb-0">
                    <i className="bi bi-file-earmark-text me-2"></i>
                    Paper Management
                </h3>
                {selectedConference && papers.length > 0 && (
                    <div className="d-flex gap-2 flex-wrap">
                        {/* Show save/discard buttons when there are pending status changes */}
                        {Object.keys(pendingStatusChanges).length > 0 && (
                            <>
                                <button 
                                    className="btn btn-primary"
                                    onClick={savePendingStatusChanges}
                                    disabled={savingChanges}
                                >
                                    {savingChanges ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-check-circle me-2"></i>
                                            Send Emails and Save Changes
                                        </>
                                    )}
                                </button>
                                <button 
                                    className="btn btn-secondary"
                                    onClick={discardPendingChanges}
                                    disabled={savingChanges}
                                >
                                    <i className="bi bi-x-circle me-2"></i>
                                    Discard Changes
                                </button>
                            </>
                        )}
                        <button 
                            className="btn btn-info"
                            onClick={handleDownloadAll}
                            disabled={loading}
                        >
                            <i className="bi bi-file-earmark-zip me-2"></i>
                            Download Manuscripts
                        </button>
                        <button 
                            className="btn btn-warning"
                            onClick={() => setShowUploadModal(true)}
                        >
                            <i className="bi bi-upload me-2"></i>
                            Bulk Update Status
                        </button>
                        <button
                            className="btn btn-outline-primary"
                            onClick={() => setShowManualAttendeeRegistrationModal(true)}
                        >
                            <i className="bi bi-person-plus me-2"></i>
                            Manual Attendee Registration
                        </button>
                        <div className="btn-group">
                            <button 
                                className="btn btn-success dropdown-toggle"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                disabled={exportingExcel}
                            >
                                {exportingExcel ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Exporting...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-download me-2"></i>
                                        Export to Excel
                                    </>
                                )}
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end">
                                <li>
                                    <button className="dropdown-item" onClick={exportToExcel} disabled={exportingExcel}>
                                        <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                                        Paper-Level
                                    </button>
                                </li>
                                <li>
                                    <button className="dropdown-item" onClick={exportAuthorsToExcel} disabled={exportingExcel}>
                                        <i className="bi bi-people me-2"></i>
                                        Author-Level
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            <div className="card-body">
                {/* Show information about pending changes */}
                {Object.keys(pendingStatusChanges).length > 0 && (
                    <div className="alert alert-warning d-flex align-items-center mb-3">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <div>
                            <strong>Warning:</strong> You have {Object.keys(pendingStatusChanges).length} unsaved status change(s). 
                            Status changes will trigger email notifications to authors. 
                            Please review and save your changes using the buttons above.
                        </div>
                    </div>
                )}
                
                {/* Error display */}
                {error && (
                    <div className="alert alert-danger">
                        <i className="bi bi-exclamation-circle me-2"></i>
                        {error}
                    </div>
                )}
                
                {/* Conference Selection */}
                <div className="row mb-2">
                    <div className="col-md-12">
                        <label htmlFor="conferenceSelect" className="form-label">
                            <strong>Select Conference:</strong>
                        </label>
                        <select
                            id="conferenceSelect"
                            className="form-select"
                            value={selectedConference}
                            onChange={(e) => setSelectedConference(e.target.value)}
                        >
                            <option value="">Select a Conference:</option>
                            {conferences.map(conference => (
                                <option key={conference._id} value={conference._id}>
                                    ({conference.status}) {conference.title}, {conference.location}, {formatDate(conference.startDate, false)} - {formatDate(conference.endDate, true)}    
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="row mb-4">

                    <div className="col-md-12 d-flex align-items-end">
                        {selectedConference && (
                            <div className="alert-info mb-0 w-100">
                                <i className="bi bi-info-circle me-2"></i>
                                {isConferenceActive() 
                                    ? 'Conference is active - paper status can be edited'
                                    : 'Conference is inactive - paper status is read-only'
                                }
                            </div>
                        )}
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="alert alert-danger" role="alert">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                    </div>
                )}

                {/* Loading Indicator */}
                {loading && (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading papers...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading papers...</p>
                    </div>
                )}

                {/* Papers Table */}
                {!loading && selectedConference && papers.length > 0 && (
                    <>
                        {/* Search Input */}
                        <div className="row mb-3">
                            <div className="col-md-12">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search papers by title, author, email, status, type, or research area..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={() => setSearchTerm('')}
                                            title="Clear search"
                                        >
                                            <i className="bi bi-x"></i>
                                        </button>
                                    )}
                                </div>
                                {searchTerm && (
                                    <small className="text-muted">
                                        Showing {filteredPapers.length} of {papers.length} papers
                                    </small>
                                )}
                            </div>
                        </div>

                        <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-dark">
                                <tr>
                                    <th>Paper #</th>
                                    <th style={titleColumnStyle}>Title</th>
                                    <th style={authorColumnStyle}>Author(S)</th>
                                    <th>Submission Date</th>
                                    <th>Registered</th>
                                    <th style={statusColumnStyle}>Status</th>
                                    <th>Files</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPapers.map(paper => {
                                    const firstAuthor = paper.authors?.[0] || {};
                                    return (
                                        <tr key={paper._id}>
                                            <td>
                                                <span className="badge bg-primary">
                                                    MS{selectedConferenceYear}~{paper.paperNumber+101 ?? 'N/A'}
                                                </span>
                                                {paper.adminNotes && (
                                                    <span 
                                                        className="badge bg-info ms-1" 
                                                        title="This paper has admin notes"
                                                    >
                                                        <i className="bi bi-sticky"></i>
                                                    </span>
                                                )}
                                            </td>
                                            <td style={titleColumnStyle}>
                                                <div className="fw-bold">{paper.manuscriptTitle}</div>
                                                <small className="text-muted">
                                                    {paper.manuscriptAbstract?.substring(0, 150)}...
                                                </small>
                                            </td>
                                            <td style={authorColumnStyle}>
                                                {(paper.authors && paper.authors.length > 0) ? (
                                                    <div className="d-flex flex-column gap-1">
                                                        {paper.authors.map((author, index) => (
                                                            <div key={author._id || `${author.email || 'author'}-${index}`}>
                                                                <div>
                                                                    
                                                                    {(author.isCorresponding || author.isCorrespondingAuthor) && (
                                                                        <span className="badge bg-info text-dark">C</span>
                                                                    )}{' '}
                                                                    <strong>{author.firstName} {author.lastName}</strong>
                                                                    <br />
                                                                    <small className="text-muted">
                                                                        {author.primaryInstitution || author.institution || author.affiliation || author.primaryAffiliation?.institution || author.university || author.organization || author.company || 'N/A'}
                                                                    </small>
                                                                    <br />
                                                                    <small>{author.country || 'N/A'}</small>                                                                    
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-muted">No authors</div>
                                                )}
                                            </td>
                                            <td>
                                                <small>{new Date(paper.submissionDate).toLocaleDateString(LOCALE, DATE_FORMAT_OPTIONS)}</small><br />
                                                <small>{new Date(paper.submissionDate).toLocaleTimeString(LOCALE, DATE_FORMAT_OPTIONS)}</small>
                                            </td>
                                            <td>
                                                {(() => {
                                                    const count = paper.registrationCount ?? 0;
                                                    const hasRegs = count > 0;
                                                    const details = Array.isArray(paper.registrationDetails)
                                                        ? paper.registrationDetails
                                                        : [];

                                                    return (
                                                        <div className="d-flex flex-column gap-1">
                                                            <span
                                                                className={`badge ${hasRegs ? 'bg-success' : 'bg-danger'}`}
                                                                title={hasRegs ? 'Confirmed registrations for this paper' : 'No confirmed registrations yet'}
                                                            >
                                                                {count}
                                                            </span>
                                                            {hasRegs && details.length > 0 && (
                                                                <div className="small text-muted">
                                                                    {details.map((detail, index) => {
                                                                        const detailDate = detail.date ? new Date(detail.date) : null;
                                                                        const dateText = detailDate
                                                                            ? `${detailDate.toLocaleDateString(LOCALE, DATE_FORMAT_OPTIONS)}`
                                                                            : 'Date N/A';
                                                                        const userText = detail.userName || 'Registered user N/A';
                                                                        const amountText = detail.amount != null
                                                                            ? `${detail.amount} ${detail.currency || ''}`.trim()
                                                                            : 'Amount N/A';
                                                                        const paypalText = detail.paypalOrderId
                                                                            ? `PPL`
                                                                            : 'Non Paypal';
                                                                        // const paypalText = detail.paypalOrderId
                                                                        //     ? `PayPal: ${detail.paypalOrderId}`
                                                                        //     : 'Other Method:';

                                                                        return (
                                                                            <div key={`${paper._id}-reg-${index}`}>
                                                                                {userText} <br />
                                                                                {dateText} <br />
                                                                                {amountText} ({paypalText})
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td style={statusColumnStyle}>
                                                {isConferenceActive() ? (
                                                    <div className="position-relative">
                                                        <select
                                                            className={`form-select form-select-sm ${pendingStatusChanges[paper._id] ? 'border-warning' : ''} ${getStatusSelectClasses(paper.status)}`}
                                                            value={paper.status}
                                                            onChange={(e) => handleStatusSelectChange(paper, e.target.value)}
                                                        >
                                                            {statusOptions.map(option => {
                                                                const optionLabel = option.label.endsWith('ed') ? option.label.slice(0, -2) : option.label;
                                                                return (
                                                                    <option key={option.value} value={option.value} style={{ backgroundColor: '#ffffff', color: '#000000' }}>
                                                                        {optionLabel}
                                                                    </option>
                                                                );
                                                            })}
                                                            <option value={RESEND_EMAIL_OPTION_VALUE} style={{ backgroundColor: '#f8f9fa', color: '#000000', fontStyle: 'italic' }}>
                                                                Resend Email
                                                            </option>
                                                        </select>
                                                        {pendingStatusChanges[paper._id] && (
                                                            <small className="text-disabled d-flex align-items-center mt-1">
                                                                <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                                                Email for status change to be sent
                                                            </small>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <small>{getStatusBadge(paper.status)}</small>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column gap-1">
                                                    {paper.files?.fullManuscript && (
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => handleFileDownload(paper, 'fullManuscript')}
                                                            type="button"
                                                        >
                                                            <i className="bi bi-download me-1"></i>
                                                            <small>Full Manuscript</small>
                                                        </button>
                                                    )}
                                                    {paper.files?.blindCopyManuscript && (
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => handleFileDownload(paper, 'blindCopyManuscript')}
                                                            type="button"
                                                        >
                                                            <i className="bi bi-download me-1"></i>
                                                            Blind Copy
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-wrap gap-1">
                                                    <button
                                                        className="btn btn-sm btn-outline-info"
                                                        style={actionButtonStyle}
                                                        onClick={() => {
                                                            setSelectedPaper(paper);
                                                            setShowDetailsModal(true);
                                                        }}
                                                        title="View Details"
                                                    >
                                                        <i className="bi bi-eye"></i>
                                                    </button>

                                                    <button
                                                        className="btn btn-sm btn-outline-warning"
                                                        style={actionButtonStyle}
                                                        onClick={() => {
                                                            setSelectedPaper(paper);
                                                            setShowEditModal(true);
                                                        }}
                                                        title="Edit Paper"
                                                    >
                                                        <i className="bi bi-pencil-square"></i>
                                                    </button>

                                                    <button 
                                                        className="btn btn-sm btn-outline-secondary"
                                                        style={actionButtonStyle}
                                                        onClick={() => {
                                                            // Send email to author
                                                            const mailto = `mailto:${firstAuthor.email}?subject=Regarding your paper: ${paper.manuscriptTitle}`;
                                                            window.open(mailto);
                                                        }}
                                                        title="Contact Author"
                                                    >
                                                        <i className="bi bi-envelope"></i>
                                                    </button>

                                                    <button 
                                                        className="btn btn-sm btn-outline-danger"
                                                        style={actionButtonStyle}
                                                        onClick={() => handleDeletePaper(paper)}
                                                        title="Delete Paper"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>

                                                    <button
                                                        className="btn btn-sm btn-outline-success"
                                                        style={actionButtonStyle}
                                                        onClick={() => {
                                                            setSelectedPaper(paper);
                                                            setShowManualRegistrationModal(true);
                                                        }}
                                                        title="Manual Register"
                                                    >
                                                        <i className="bi bi-person-check"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                    </>
                )}

                {/* No Papers Message */}
                {!loading && selectedConference && papers.length === 0 && (
                    <div className="text-center py-5">
                        <i className="bi bi-file-earmark-text display-1 text-muted"></i>
                        <h5 className="mt-3 text-muted">No Papers Found</h5>
                        <p className="text-muted">
                            No papers have been submitted to this conference yet.
                        </p>
                    </div>
                )}

                {/* No Search Results Message */}
                {!loading && selectedConference && papers.length > 0 && filteredPapers.length === 0 && (
                    <div className="text-center py-5">
                        <i className="bi bi-search display-1 text-muted"></i>
                        <h5 className="mt-3 text-muted">No Papers Match Your Search</h5>
                        <p className="text-muted">
                            Try adjusting your search terms or <button 
                                className="btn btn-link p-0 text-decoration-underline" 
                                onClick={() => setSearchTerm('')}
                            >
                                clear the search
                            </button> to see all papers.
                        </p>
                    </div>
                )}

                {/* No Conference Selected */}
                {!selectedConference && (
                    <div className="text-center py-5">
                        <i className="bi bi-arrow-up display-1 text-muted"></i>
                        <h5 className="mt-3 text-muted">Select a Conference</h5>
                        <p className="text-muted">
                            Please select a conference from the dropdown above to view and manage papers.
                        </p>
                    </div>
                )}
            </div>

            {/* Paper Details Modal */}
            <PaperDetailsModal
                paper={selectedPaper}
                conference={selectedConference && conferences.length > 0 ? conferences.find(conf => conf._id === selectedConference) : null}
                show={showDetailsModal}
                onHide={() => {
                    setShowDetailsModal(false);
                    setSelectedPaper(null);
                }}
            />

            {/* Paper Edit Modal */}
            <PaperEditModal
                paper={selectedPaper}
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setSelectedPaper(null);
                }}
                onPaperUpdated={handlePaperUpdated}
            />

            <ManualRegistrationModal
                paper={selectedPaper}
                conference={selectedConference && conferences.length > 0 ? conferences.find(conf => conf._id === selectedConference) : null}
                onClose={() => {
                    setShowManualRegistrationModal(false);
                    setSelectedPaper(null);
                }}
                onSuccess={() => {
                    setShowManualRegistrationModal(false);
                    setSelectedPaper(null);
                    loadPapers();
                }}
                show={showManualRegistrationModal}
            />

            <ManualAttendeeRegistrationModal
                conference={selectedConference && conferences.length > 0 ? conferences.find(conf => conf._id === selectedConference) : null}
                onClose={() => {
                    setShowManualAttendeeRegistrationModal(false);
                }}
                onSuccess={() => {
                    setShowManualAttendeeRegistrationModal(false);
                }}
                show={showManualAttendeeRegistrationModal}
            />

            {/* Bulk Upload Modal */}
            {showUploadModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="bi bi-upload me-2"></i>
                                    Bulk Update Paper Status
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowUploadModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-info">
                                    <i className="bi bi-info-circle me-2"></i>
                                    Upload an Excel file with paper IDs and new status values. 
                                    The file should have columns: <strong>id</strong> and <strong>Status</strong>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label">Valid Status Values:</label>
                                    <div className="d-flex flex-wrap gap-1">
                                        {statusOptions.map(option => {
                                            const optionKey = option.value.endsWith('ed') ? option.value.slice(0, -2) : option.value;
                                            return (
                                                <span key={optionKey} className={`badge ${option.className} text-white`}>
                                                    {option.value}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="excelFile" className="form-label">
                                        Select Excel File (.xlsx, .xls):
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        id="excelFile"
                                        accept=".xlsx,.xls,.csv"
                                        onChange={(e) => setUploadFile(e.target.files[0])}
                                        disabled={uploading}
                                    />
                                </div>

                                {uploadFile && (
                                    <div className="alert alert-success">
                                        <i className="bi bi-file-earmark-check me-2"></i>
                                        File selected: {uploadFile.name}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowUploadModal(false)}
                                    disabled={uploading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary" 
                                    onClick={handleBulkUpload}
                                    disabled={!uploadFile || uploading}
                                >
                                    {uploading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-upload me-2"></i>
                                            Upload & Update
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Results Modal */}
            {uploadResults && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="bi bi-check-circle me-2"></i>
                                    Bulk Update Results
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setUploadResults(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-3">
                                        <div className="card text-center">
                                            <div className="card-body">
                                                <h5 className="card-title text-success">{uploadResults.summary.successful}</h5>
                                                <p className="card-text">Successful</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card text-center">
                                            <div className="card-body">
                                                <h5 className="card-title text-danger">{uploadResults.summary.failed}</h5>
                                                <p className="card-text">Failed</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card text-center">
                                            <div className="card-body">
                                                <h5 className="card-title text-warning">{uploadResults.summary.skipped}</h5>
                                                <p className="card-text">Skipped</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card text-center">
                                            <div className="card-body">
                                                <h5 className="card-title text-info">{uploadResults.summary.totalRows}</h5>
                                                <p className="card-text">Total Rows</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {uploadResults.results.failed.length > 0 && (
                                    <div className="mb-3">
                                        <h6 className="text-danger">Failed Updates:</h6>
                                        <div className="table-responsive">
                                            <table className="table table-sm">
                                                <thead>
                                                    <tr>
                                                        <th>Paper ID</th>
                                                        <th>Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {uploadResults.results.failed.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>{item.paperId}</td>
                                                            <td>{item.reason}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {uploadResults.results.successful.length > 0 && (
                                    <div className="mb-3">
                                        <h6 className="text-success">Successful Updates:</h6>
                                        <div className="table-responsive">
                                            <table className="table table-sm">
                                                <thead>
                                                    <tr>
                                                        <th>Paper ID</th>
                                                        <th>Title</th>
                                                        <th>New Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {uploadResults.results.successful.slice(0, 10).map((item, index) => (
                                                        <tr key={index}>
                                                            <td>{item.paperId}</td>
                                                            <td>{item.title?.substring(0, 50)}...</td>
                                                            <td>
                                                                <span className="badge bg-success">
                                                                    {item.newStatus}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {uploadResults.results.successful.length > 10 && (
                                                <p className="text-muted">
                                                    Showing first 10 of {uploadResults.results.successful.length} successful updates
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-primary" 
                                    onClick={() => setUploadResults(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaperManagement;
